"use client"

import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { Textarea } from "@/shared/components/ui/textarea"
import { Slider } from "@/shared/components/ui/slider"
import { useGenerationStore } from "../store"
import { GUIDANCE_SCALE, NEGATIVE_PROMPT_MAX_LENGTH } from "../constants"

export function AdvancedSettings() {
  const t = useTranslations("generate")
  const toggleAdvanced = useGenerationStore((s) => s.toggleAdvanced)
  const negativePrompt = useGenerationStore((s) => s.negativePrompt)
  const setNegativePrompt = useGenerationStore((s) => s.setNegativePrompt)
  const guidanceScale = useGenerationStore((s) => s.guidanceScale)
  const setGuidanceScale = useGenerationStore((s) => s.setGuidanceScale)

  return (
    <div className="mt-2 overflow-hidden rounded-2xl border border-white/[0.1] bg-card/78 shadow-[0_18px_48px_rgba(0,0,0,0.42)] backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.06] text-zinc-300">
            <Icon name="setting" size={14} className="size-3.5" aria-hidden />
          </span>
          <p className="truncate text-sm font-semibold text-white">{t("advanced.title")}</p>
        </div>
        <button
          type="button"
          onClick={toggleAdvanced}
          className="flex size-8 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-zinc-400 transition-colors hover:bg-white/[0.08] hover:text-white"
          aria-label={t("advanced.close")}
        >
          <Icon name="x" size={14} className="size-3.5" aria-hidden />
        </button>
      </div>

      <div className="grid gap-4 p-4 sm:grid-cols-[1.25fr_0.75fr]">
        <div className="min-w-0 space-y-2">
          <label className="text-xs font-semibold text-zinc-400">
            {t("advanced.negativePrompt")}
          </label>
          <Textarea
            placeholder={t("advanced.negativePromptPlaceholder")}
            value={negativePrompt}
            onChange={(e) =>
              setNegativePrompt(
                e.target.value.slice(0, NEGATIVE_PROMPT_MAX_LENGTH)
              )
            }
            className="min-h-[76px] resize-none rounded-xl border-white/[0.08] bg-black/20 text-[16px] text-white placeholder:text-zinc-600 focus-visible:border-[#5aa7ff]/60 sm:text-sm"
            aria-label={t("advanced.negativePromptLabel")}
          />
          <div className="flex justify-end text-[10px] tabular-nums text-zinc-600">
            {negativePrompt.length}/{NEGATIVE_PROMPT_MAX_LENGTH}
          </div>
        </div>

        <div className="flex min-w-0 flex-col justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.035] p-3">
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs font-semibold text-zinc-400">
              {t("advanced.guidance")}
            </label>
            <span className="rounded-full border border-white/[0.08] bg-black/20 px-2 py-0.5 text-xs font-semibold tabular-nums text-white">
              {guidanceScale.toFixed(1)}
            </span>
          </div>
          <Slider
            value={[guidanceScale]}
            onValueChange={([val]) => setGuidanceScale(val)}
            min={GUIDANCE_SCALE.min}
            max={GUIDANCE_SCALE.max}
            step={GUIDANCE_SCALE.step}
            aria-label={t("advanced.guidanceScale")}
          />
          <div className="flex justify-between text-[10px] font-medium text-zinc-600">
            <span>{t("advanced.creative")}</span>
            <span>{t("advanced.precise")}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
